import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  User, Wrench, Shield, Check, Eye, EyeOff, 
  ArrowRight, Menu, X, Loader2 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, signup, user } = useAuth();
  const [mode, setMode] = useState(searchParams.get('mode') || 'login');
  const [role, setRole] = useState(searchParams.get('mode') === 'contractor' ? 'contractor' : 'customer');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const returnTo = searchParams.get('returnTo');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    country: 'UK'
  });

  // Set initial mode based on URL
  useEffect(() => {
    const urlMode = searchParams.get('mode');
    if (urlMode === 'contractor') {
      setMode('signup');
      setRole('contractor');
    } else if (urlMode === 'signup') {
      setMode('signup');
    } else if (urlMode === 'login') {
      setMode('login');
    }
  }, [searchParams]);

  // Check if user is already logged in and has pending quote
  useEffect(() => {
    if (user && returnTo === 'confirm-quote') {
      handleQuoteConfirmation(user);
    }
  }, [user]);

  const handleQuoteConfirmation = async (loggedInUser) => {
    const pendingQuote = sessionStorage.getItem('pendingQuote');
    if (pendingQuote && loggedInUser.role === 'customer') {
      try {
        const quote = JSON.parse(pendingQuote);
        const response = await axios.post(`${API_URL}/jobs/confirm-from-quote`, null, {
          params: {
            quote_id: quote.quote_id,
            quote_token: quote.quote_token
          }
        });
        
        sessionStorage.removeItem('pendingQuote');
        sessionStorage.removeItem('quoteFormData');
        
        toast.success('Booking created! Proceed to payment.');
        navigate(`/jobs/${response.data.job_id}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to confirm booking');
        navigate('/dashboard');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        const result = await login(formData.email, formData.password);
        
        if (!result.success) {
          toast.error(result.error || 'Login failed');
          return;
        }
        
        const loggedInUser = result.user;
        toast.success('Welcome back!');
        
        if (returnTo === 'confirm-quote') {
          await handleQuoteConfirmation(loggedInUser);
          return;
        }
        
        // Use active_role for routing (supports role switching)
        const userRole = loggedInUser.active_role || loggedInUser.role?.toLowerCase?.() || loggedInUser.role;
        
        if (userRole === 'customer') {
          navigate('/dashboard');
        } else if (userRole === 'contractor') {
          navigate('/contractor/dashboard');
        } else {
          navigate('/admin');
        }
      } else {
        const result = await signup({ ...formData, role });
        
        if (!result.success) {
          toast.error(result.error || 'Signup failed');
          return;
        }
        
        const newUser = result.user;
        toast.success('Account created!');
        
        if (returnTo === 'confirm-quote' && role === 'customer') {
          await handleQuoteConfirmation(newUser);
          return;
        }
        
        if (role === 'customer') {
          navigate('/dashboard');
        } else {
          navigate('/contractor/onboarding');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Navigation - Same as Homepage */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ZOLT</Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/#how-it-works" className="text-neutral-600 hover:text-black text-sm font-medium">How it works</Link>
                <Link to="/#pricing" className="text-neutral-600 hover:text-black text-sm font-medium">Pricing</Link>
                <Link to="/auth?mode=contractor" className="text-neutral-600 hover:text-black text-sm font-medium">Become a contractor</Link>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => { setMode('login'); setRole('customer'); }}
                className={`text-neutral-700 ${mode === 'login' ? 'bg-neutral-100' : ''}`}
              >
                Log in
              </Button>
              <Button 
                onClick={() => { setMode('signup'); setRole('customer'); }}
                className={`rounded-full px-6 ${mode === 'signup' && role === 'customer' ? 'bg-black text-white' : 'bg-neutral-100 text-black hover:bg-neutral-200'}`}
              >
                Sign up
              </Button>
            </div>
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-neutral-100"
            >
              <div className="px-4 py-4 space-y-3">
                <Link to="/#how-it-works" className="block py-2 text-neutral-600">How it works</Link>
                <Link to="/#pricing" className="block py-2 text-neutral-600">Pricing</Link>
                <Link to="/auth?mode=contractor" className="block py-2 text-neutral-600">Become a contractor</Link>
                <hr className="border-neutral-200" />
                <Button 
                  variant="outline" 
                  onClick={() => { setMode('login'); setMobileMenuOpen(false); }}
                  className="w-full"
                >
                  Log in
                </Button>
                <Button 
                  onClick={() => { setMode('signup'); setMobileMenuOpen(false); }}
                  className="w-full bg-black text-white"
                >
                  Sign up
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-200px)]">
            
            {/* Left: Auth Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="order-2 lg:order-1"
            >
              <div className="bg-white rounded-3xl shadow-2xl shadow-black/5 border border-neutral-100 p-8 max-w-md mx-auto lg:mx-0">
                
                {/* Mode Toggle */}
                <div className="flex bg-neutral-100 rounded-xl p-1 mb-8">
                  <button
                    onClick={() => setMode('login')}
                    className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                      mode === 'login' 
                        ? 'bg-white shadow-sm text-black' 
                        : 'text-neutral-500 hover:text-neutral-700'
                    }`}
                  >
                    Log in
                  </button>
                  <button
                    onClick={() => setMode('signup')}
                    className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                      mode === 'signup' 
                        ? 'bg-white shadow-sm text-black' 
                        : 'text-neutral-500 hover:text-neutral-700'
                    }`}
                  >
                    Sign up
                  </button>
                </div>

                <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {mode === 'login' ? 'Welcome back' : role === 'contractor' ? 'Become a ZOLT Pro' : 'Create account'}
                </h1>
                <p className="text-neutral-500 mb-8">
                  {mode === 'login' 
                    ? 'Enter your credentials to access your account' 
                    : role === 'contractor' 
                      ? 'Join our network of verified contractors'
                      : 'Get instant quotes and book verified contractors'
                  }
                </p>

                {/* Role Selection for Signup */}
                {mode === 'signup' && (
                  <div className="mb-6">
                    <Label className="text-sm font-medium text-neutral-700 mb-3 block">I want to</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setRole('customer')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          role === 'customer'
                            ? 'border-black bg-black text-white'
                            : 'border-neutral-200 hover:border-neutral-400'
                        }`}
                        data-testid="role-customer-btn"
                      >
                        <User className={`w-6 h-6 mb-2 ${role === 'customer' ? 'text-white' : 'text-neutral-600'}`} />
                        <div className="font-semibold">Book services</div>
                        <div className={`text-xs ${role === 'customer' ? 'text-neutral-300' : 'text-neutral-500'}`}>
                          I need a contractor
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setRole('contractor')}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          role === 'contractor'
                            ? 'border-black bg-black text-white'
                            : 'border-neutral-200 hover:border-neutral-400'
                        }`}
                        data-testid="role-contractor-btn"
                      >
                        <Wrench className={`w-6 h-6 mb-2 ${role === 'contractor' ? 'text-white' : 'text-neutral-600'}`} />
                        <div className="font-semibold">Offer services</div>
                        <div className={`text-xs ${role === 'contractor' ? 'text-neutral-300' : 'text-neutral-500'}`}>
                          I'm a contractor
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode === 'signup' && (
                    <>
                      <div>
                        <Label htmlFor="name" className="text-sm font-medium text-neutral-700">Full Name</Label>
                        <Input
                          id="name"
                          data-testid="name-input"
                          type="text"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="h-12 mt-1.5 border-neutral-200 focus:border-black focus:ring-black"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-sm font-medium text-neutral-700">Phone Number</Label>
                        <Input
                          id="phone"
                          data-testid="phone-input"
                          type="tel"
                          placeholder="+44 7XXX XXXXXX"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="h-12 mt-1.5 border-neutral-200 focus:border-black focus:ring-black"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-neutral-700">Email</Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12 mt-1.5 border-neutral-200 focus:border-black focus:ring-black"
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-sm font-medium text-neutral-700">Password</Label>
                    <div className="relative mt-1.5">
                      <Input
                        id="password"
                        data-testid="password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        className="h-12 pr-10 border-neutral-200 focus:border-black focus:ring-black"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-btn"
                    disabled={loading}
                    className="w-full h-14 bg-black hover:bg-neutral-800 text-white rounded-xl text-lg mt-6"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Please wait...
                      </>
                    ) : (
                      <>
                        {mode === 'login' ? 'Log in' : 'Create account'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-neutral-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-neutral-500">or</span>
                  </div>
                </div>

                {/* Toggle Mode Link */}
                <p className="text-center text-neutral-600">
                  {mode === 'login' ? (
                    <>
                      Don't have an account?{' '}
                      <button
                        onClick={() => setMode('signup')}
                        className="text-black font-semibold hover:underline"
                        data-testid="toggle-mode-btn"
                      >
                        Sign up
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <button
                        onClick={() => setMode('login')}
                        className="text-black font-semibold hover:underline"
                        data-testid="toggle-mode-btn"
                      >
                        Log in
                      </button>
                    </>
                  )}
                </p>
              </div>
            </motion.div>

            {/* Right: Hero Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="order-1 lg:order-2 text-center lg:text-left"
            >
              {role === 'contractor' && mode === 'signup' ? (
                <>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Grow your business
                    <br />
                    <span className="text-neutral-400">with ZOLT</span>
                  </h2>
                  <p className="text-xl text-neutral-600 mb-8 max-w-lg mx-auto lg:mx-0">
                    Join thousands of verified contractors earning more with fair, transparent pricing and instant job dispatch.
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    {[
                      'Get instant job notifications in your area',
                      'Set your own service radius and availability',
                      'No subscription fees - only pay when you earn',
                      'Build your reputation with verified reviews'
                    ].map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-3 justify-center lg:justify-start">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-neutral-700">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-neutral-50 rounded-2xl p-6 max-w-md mx-auto lg:mx-0">
                    <div className="flex items-center gap-3 mb-4">
                      <Shield className="w-6 h-6 text-neutral-600" />
                      <span className="font-semibold">Verification & Trust</span>
                    </div>
                    <p className="text-sm text-neutral-600">
                      You can start onboarding now. Verification is required to display trust badges and receive certain job types.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Fix anything.
                    <br />
                    <span className="text-neutral-400">Anytime.</span>
                  </h2>
                  <p className="text-xl text-neutral-600 mb-8 max-w-lg mx-auto lg:mx-0">
                    Get instant quotes, book verified contractors, and track everything in real-time.
                  </p>
                  
                  <div className="flex flex-wrap gap-6 justify-center lg:justify-start mb-8">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="text-neutral-700">Upfront pricing</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="text-neutral-700">Verified contractors</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="text-neutral-700">Real-time tracking</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-6 p-6 bg-neutral-50 rounded-2xl max-w-md mx-auto lg:mx-0">
                    <div>
                      <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>15k+</div>
                      <div className="text-sm text-neutral-500">Jobs done</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>4.9</div>
                      <div className="text-sm text-neutral-500">Avg. rating</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>&lt;30m</div>
                      <div className="text-sm text-neutral-500">Response</div>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </main>

      {/* Footer - Same as Homepage */}
      <footer className="py-12 bg-neutral-50 border-t border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ZOLT</div>
            <div className="flex gap-6 text-sm text-neutral-600">
              <Link to="/#how-it-works" className="hover:text-black">How it works</Link>
              <Link to="/#pricing" className="hover:text-black">Pricing</Link>
              <a href="#" className="hover:text-black">Privacy</a>
              <a href="#" className="hover:text-black">Terms</a>
            </div>
            <p className="text-sm text-neutral-500">© 2026 ZOLT X Limited</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthPage;
